import { useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Text, Flex } from '@frontend/legacy-components';

import {
  InteractiveFlexAsLink,
  InteractiveFlex,
} from 'components/lib2/InteractiveContainers';

import { PopoutContext } from 'modules/notifications/hooks/PopoutContext.js';

import { getGroupPrimaryKey } from 'store/session/selectors';

import { acknowledgeNotification } from '../data/actions';
import type { Notification } from '../types/index';
import { NotificationCircle } from './subcomponents/NotificationCircle';
import NotificationDateAndBusinessName from './subcomponents/NotificationDateAndBusinessName';
import NotificationErrorBoundary from './subcomponents/NotificationErrorBoundary';
import NotificationMessage from './subcomponents/NotificationMessage';
import NotificationsPopover from './subcomponents/NotificationPopover';
import NotificationThumbnail from './subcomponents/NotificationThumbnail';

const NotificationComponent = ({
  notification,
  otherOccurrence,
  otherOccurrences,
}: {
  notification: Notification;
  otherOccurrence?: string;
  otherOccurrences?: string;
}) => {
  const dispatch = useDispatch();
  const { isPopout, closePopout } = useContext(PopoutContext);

  const [isOpen, setIsOpen] = useState(false);

  const groupId = useSelector(getGroupPrimaryKey);

  const { id: notificationGroupId } = notification.group;

  const handleClick = () => {
    if (groupId !== notificationGroupId) setIsOpen(true);
    else {
      dispatch(acknowledgeNotification.request({ notification }));
      isPopout && closePopout();
    }
  };

  const otherOccurrencesCount = notification.otherOccurrencesCount;
  const isOther = otherOccurrencesCount > 0 && otherOccurrence;

  return (
    <NotificationErrorBoundary>
      {groupId === notificationGroupId ? (
        <InteractiveFlexAsLink
          onClick={handleClick}
          cursor="pointer"
          to={notification?.relativeUrl || ''}
          p={2}
        >
          <NotificationCircle
            notification={notification}
            isCurrent={groupId === notificationGroupId ? true : false}
          />
          <Flex
            flexDirection="column"
            width="100%"
            minWidth={0}
            marginRight={2}
          >
            <NotificationMessage notification={notification} />
            {isOther && (
              <Text fontSize="11px">
                {`And ${otherOccurrencesCount} other ${
                  otherOccurrencesCount === 1
                    ? otherOccurrence
                    : otherOccurrences
                }`}
              </Text>
            )}
            <NotificationDateAndBusinessName notification={notification} />
          </Flex>
          {notification.partFileUuid && (
            <NotificationThumbnail notification={notification} />
          )}
        </InteractiveFlexAsLink>
      ) : (
        <>
          <NotificationsPopover
            toggleOpen={setIsOpen}
            isOpen={isOpen}
            nextUrl={notification?.relativeUrl || ''}
            notification={notification}
          >
            <InteractiveFlex onClick={handleClick} cursor="pointer" p={2}>
              <NotificationCircle
                notification={notification}
                isCurrent={groupId === notificationGroupId ? true : false}
              />
              <Flex
                flexDirection="column"
                width="100%"
                minWidth={0}
                marginRight={2}
              >
                <NotificationMessage notification={notification} />
                {isOther && (
                  <Text fontSize="11px">
                    {`And ${otherOccurrencesCount} other ${
                      otherOccurrencesCount === 1
                        ? otherOccurrence
                        : otherOccurrences
                    }`}
                  </Text>
                )}
                <NotificationDateAndBusinessName notification={notification} />
              </Flex>
              {notification.partFileUuid && (
                <NotificationThumbnail notification={notification} />
              )}
            </InteractiveFlex>
          </NotificationsPopover>
        </>
      )}
    </NotificationErrorBoundary>
  );
};

export default NotificationComponent;
